import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Swords, Search, ChevronRight, Radio, Flag, Shield } from "lucide-react";
import { api } from "../lib/api";
import { Loader, Empty } from "../components/UI";
import { ExhibitionBadge } from "../components/Arena";
import { ClubCrest } from "../components/ClubCrest";

const WRAP = "mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12";
const LIMIT = 24;

function isFinal(m) { return m.status === "FINAL"; }
function isLive(m) { return !isFinal(m) && m.status !== "CANCELLED"; }
function isNT(c) { return !!(c && c.is_national); }

function dayKey(iso) { try { return new Date(iso).toDateString(); } catch { return "—"; } }
function dayLabel(iso) {
  try {
    const d = new Date(iso); const t = new Date();
    const y = new Date(t); y.setDate(t.getDate() - 1);
    if (d.toDateString() === t.toDateString()) return "Today";
    if (d.toDateString() === y.toDateString()) return "Yesterday";
    return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch { return "Earlier"; }
}
function timeOf(iso) { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } }

export default function MatchesList() {
  const [items, setItems] = useState(null);     // loaded (accumulated) page items
  const [total, setTotal] = useState(0);         // TRUE total from server count
  const [comps, setComps] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [status, setStatus] = useState("all");   // all | live | final
  const [type, setType] = useState("all");        // all | clubs | nt
  const [comp, setComp] = useState("all");
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");               // debounced search term
  const [skip, setSkip] = useState(0);
  const reqId = useRef(0);

  // competition options come from the FULL competitions registry (not a truncated page)
  useEffect(() => {
    api.get("/competitions").then(({ data }) => {
      const map = {};
      (data.competitions || []).forEach((c) => { map[c.id] = { name: c.name, type: c.competition_type }; });
      setComps(map);
    }).catch(() => {});
  }, []);

  // debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setDq(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // reset to page 1 whenever a filter changes
  useEffect(() => { setSkip(0); }, [status, type, comp, dq]);

  const params = useMemo(() => {
    const p = { skip, limit: LIMIT };
    if (status !== "all") p.status = status;
    if (type !== "all") p.type = type;
    if (comp !== "all") p.competition_id = comp;
    if (dq) p.search = dq;
    return p;
  }, [skip, status, type, comp, dq]);

  // server-driven fetch: replace on page 1, append on load-more
  useEffect(() => {
    const mine = ++reqId.current;
    if (skip === 0) setItems(null);
    else setLoadingMore(true);
    api.get("/matches", { params }).then(({ data }) => {
      if (mine !== reqId.current) return; // ignore stale responses
      setTotal(data.total || 0);
      setItems((prev) => (skip === 0 || !prev ? (data.items || []) : [...prev, ...(data.items || [])]));
    }).catch(() => {
      if (mine !== reqId.current) return;
      setItems((prev) => prev || []);
    }).finally(() => { if (mine === reqId.current) setLoadingMore(false); });
  }, [params, skip]);

  const compName = useCallback((id) => comps[id]?.name || "Competition", [comps]);

  // group the currently-loaded items by day (Phase-12 IA)
  const groups = useMemo(() => {
    const g = []; const idx = {};
    (items || []).forEach((m) => {
      const k = dayKey(m.scheduled_date || m.submission_open);
      if (!(k in idx)) { idx[k] = g.length; g.push({ key: k, label: dayLabel(m.scheduled_date || m.submission_open), items: [] }); }
      g[idx[k]].items.push(m);
    });
    return g;
  }, [items]);

  const loaded = items ? items.length : 0;
  const hasMore = loaded < total;
  const compOptions = Object.entries(comps);

  return (
    <div className="animate-fade-up pb-24 overflow-x-hidden">
      {/* COMPACT HEADER */}
      <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.22)] bg-[#000000]">
        <div className="absolute inset-0 mesh-grid opacity-[0.12] pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(55% 120% at 15% -10%, rgba(212,175,55,0.18) 0%, transparent 55%)" }} />
        <div className={`${WRAP} relative py-9 sm:py-11`}>
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#E5B842] font-mono font-bold mb-3 flex items-center gap-2">
            <Swords className="w-3.5 h-3.5" /> WSTF Match Center
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-none wsbf-slant-block">Matches</h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-400">Official ArenaBook competition matches across the WSTF ecosystem — two teams, 11 blind coupons each, paired and revealed into Team Points.</p>
        </div>
      </section>

      <div className={`${WRAP} pt-7`}>
        {/* FILTER BAR */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="match-filters">
          <div className="inline-flex gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512]">
            {[["all", "All"], ["live", "Live"], ["final", "Final"]].map(([k, l]) => (
              <button key={k} data-testid={`match-status-filter-${k}`} onClick={() => setStatus(k)}
                className={`px-3 py-1.5 rounded-lg text-xs font-heading font-bold uppercase inline-flex items-center gap-1.5 ${status === k ? "bg-[#E5B842] text-[#000000]" : "text-slate-300 hover:text-[#E5B842]"}`}
                style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
                {k === "live" && <Radio className="w-3 h-3" />}{l}
              </button>
            ))}
          </div>
          <div className="inline-flex gap-1 p-1 rounded-xl bg-[#0a0a0a] border border-[#2b2512]">
            {[["all", "All"], ["clubs", "Clubs"], ["nt", "Nat. Teams"]].map(([k, l]) => (
              <button key={k} data-testid={`match-type-filter-${k}`} onClick={() => setType(k)}
                className={`px-3 py-1.5 rounded-lg text-xs font-heading font-bold uppercase ${type === k ? "bg-[#E5B842] text-[#000000]" : "text-slate-300 hover:text-[#E5B842]"}`}
                style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>{l}</button>
            ))}
          </div>
          {compOptions.length > 1 && (
            <select data-testid="match-competition-filter" value={comp} onChange={(e) => setComp(e.target.value)}
              className="bg-[#0a0a0a] border border-[#2b2512] rounded-lg px-3 py-2 text-sm text-slate-200 hover:border-[#E5B842]/40" style={{ transition: "border-color 0.15s ease" }}>
              <option value="all">All competitions</option>
              {compOptions.map(([id, c]) => <option key={id} value={id}>{c.name}</option>)}
            </select>
          )}
          <div className="relative ml-auto">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="match-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team / match ID"
              className="bg-[#0a0a0a] border border-[#2b2512] rounded-lg pl-9 pr-3 py-2 text-sm text-slate-200 w-56 focus:border-[#E5B842]/50 outline-none" style={{ transition: "border-color 0.15s ease" }} />
          </div>
        </div>

        <div className="text-xs font-mono text-slate-500 mb-4" data-testid="matches-total">
          {total.toLocaleString()} {total === 1 ? "match" : "matches"}
          {loaded > 0 && loaded < total ? ` · showing ${loaded.toLocaleString()}` : ""}
        </div>

        {items === null ? <Loader label="Loading match center…" /> : (
          items.length === 0 ? <Empty label="No matches for this filter" /> : (
            <div className="space-y-8" data-testid="matches-list">
              {groups.map((g) => (
                <div key={g.key}>
                  <div className="flex items-center gap-2 mb-3 text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500">
                    <span className="h-px flex-1 bg-[#2b2512]" />{g.label}<span className="h-px flex-1 bg-[#2b2512]" />
                  </div>
                  <div className="space-y-2">
                    {g.items.map((m) => <MatchRow key={m.id} m={m} compName={compName(m.competition_id)} />)}
                  </div>
                </div>
              ))}
              {hasMore && (
                <div className="flex justify-center pt-2">
                  <button data-testid="match-load-more" disabled={loadingMore} onClick={() => setSkip((s) => s + LIMIT)}
                    className="px-5 py-2.5 rounded-lg text-sm font-bold uppercase tracking-wide bg-[#161616] border border-[#2b2512] text-slate-200 hover:border-[#E5B842]/50 disabled:opacity-50"
                    style={{ transition: "border-color 0.15s ease" }}>
                    {loadingMore ? "Loading…" : `Load more (${(total - loaded).toLocaleString()} remaining)`}
                  </button>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function MatchRow({ m, compName }) {
  const fin = isFinal(m);
  const live = isLive(m);
  const nt = isNT(m.home_club);
  const hp = m.home_team_points, ap = m.away_team_points;
  const homeWin = fin && m.match_result?.result_type === "HOME_WIN";
  const awayWin = fin && m.match_result?.result_type === "AWAY_WIN";
  return (
    <Link to={`/match/${m.id}`} data-testid={`match-card-${m.public_match_id}`}
      className="group grid grid-cols-[auto_1fr_auto] md:grid-cols-[132px_1fr_auto_1fr_200px_auto] items-center gap-x-3 gap-y-2 rounded-xl bg-[#0f0f0f] border border-[#2b2512] px-4 py-3 hover:border-[#E5B842]/40"
      style={{ transition: "border-color 0.15s ease" }}>
      {/* status + time */}
      <div className="flex flex-col gap-1 items-start min-w-0">
        {fin ? <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-500">Final</span>
          : <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider text-emerald-400"><Radio className="w-3 h-3" />{m.status.replace(/_/g, " ")}</span>}
        <span className="text-[10px] font-mono text-slate-600">{timeOf(m.scheduled_date || m.submission_open)}</span>
      </div>

      {/* home */}
      <div className="flex items-center gap-2.5 min-w-0 md:justify-end md:text-right md:flex-row-reverse">
        <ClubCrest club={m.home_club} size={34} />
        <span className={`font-heading font-bold uppercase tracking-tight truncate ${homeWin ? "text-[#E5B842]" : "text-slate-100"}`}>{m.home_club?.name}</span>
      </div>

      {/* score / vs */}
      <div className="flex flex-col items-center shrink-0 px-1">
        {fin || (live && (hp != null)) ? (
          <>
            <div className="font-heading text-lg font-black tabular-nums text-slate-50 whitespace-nowrap">
              <span className={homeWin ? "text-[#E5B842]" : ""}>{hp ?? 0}</span>
              <span className="text-slate-600 mx-1">–</span>
              <span className={awayWin ? "text-[#E5B842]" : ""}>{ap ?? 0}</span>
            </div>
            <span className="text-[8px] font-mono uppercase tracking-[0.15em] text-slate-600">Team Pts</span>
          </>
        ) : (
          <span className="font-heading text-xs font-bold uppercase text-slate-500">vs</span>
        )}
      </div>

      {/* away */}
      <div className="flex items-center gap-2.5 min-w-0">
        <ClubCrest club={m.away_club} size={34} />
        <span className={`font-heading font-bold uppercase tracking-tight truncate ${awayWin ? "text-[#E5B842]" : "text-slate-100"}`}>{m.away_club?.name}</span>
      </div>

      {/* competition + round */}
      <div className="hidden md:flex flex-col min-w-0">
        <span className="text-[11px] text-slate-300 truncate flex items-center gap-1.5">
          {nt ? <Flag className="w-3 h-3 text-slate-500 shrink-0" /> : <Shield className="w-3 h-3 text-slate-500 shrink-0" />}
          {compName}
        </span>
        <span className="text-[10px] font-mono text-slate-600">{m.matchday ? `Matchday ${m.matchday}` : m.round ? `Round ${m.round}` : m.context_label || ""}</span>
      </div>

      {/* chevron */}
      <div className="flex items-center gap-2 justify-self-end col-span-3 md:col-span-1 justify-between md:justify-end w-full md:w-auto">
        {m.is_exhibition && <ExhibitionBadge />}
        <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-slate-500 group-hover:text-[#E5B842]" style={{ transition: "color 0.15s ease" }}>
          View <ChevronRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}
